import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GzAppShell, GzNavItem } from '@gozem/ui';

import { AuthService } from '../../core/services/auth.service';
import { RouteNavigator } from '../../core/services/route-navigator.service';
import { MerchantStore } from '../../core/stores';
import { NAV_SECTIONS } from '../../config/nav-sections.config';
import { AccountSwitcherComponent } from '../navbar/account-switcher/account-switcher.component';
import { NotificationComponent } from '../../shared/components/notification/notification.component';

/**
 * MainLayout
 *
 * Authenticated shell built on top of the GzAppShell library component.
 * Self-contained: brand, navigation, and topbar are all defined here.
 *
 * Slots used:
 *   brand   — Gozem brand mark + title
 *   nav     — section-grouped GzNavItem entries
 *   topbar  — mobile hamburger · AccountSwitcher · notifications · profile
 *
 * Mobile drawer is delegated to GzAppShell.openMobile() / closeMobile().
 * Active-route state is tracked via a Router.events signal (no routerLinkActive).
 */
@Component({
  selector: 'app-main-layout',
  imports: [
    RouterModule,
    NgOptimizedImage,
    GzAppShell,
    GzNavItem,
    AccountSwitcherComponent,
    NotificationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.css',
  host: {
    '(document:keydown.escape)': 'closeAllMenus()',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class MainLayout {
  private readonly authService = inject(AuthService);
  private readonly merchantStore = inject(MerchantStore);
  private readonly router = inject(Router);
  private readonly routeNavigator = inject(RouteNavigator);

  /** Reference to the shell for programmatic mobile-drawer control. */
  protected readonly appShell = viewChild.required(GzAppShell);

  protected readonly sections = NAV_SECTIONS;

  // ── Active-route tracking (no routerLinkActive — driven by signal) ─────────
  private readonly currentUrl = signal(this.router.url);

  // ── Topbar dropdown state ──────────────────────────────────────────────────
  protected readonly showNotifications = signal(false);
  protected readonly showProfile = signal(false);

  // ── Merchant / auth data ───────────────────────────────────────────────────
  protected readonly unreadNotifications = this.merchantStore.unreadNotifications;
  protected readonly currentUser = this.authService.currentUser;
  protected readonly merchantEmail = this.merchantStore.email;

  protected readonly profileDisplayName = computed(() => {
    const name = this.merchantStore.displayName();
    if (name && name !== 'Merchant') return name;
    const email = this.currentUser()?.email;
    if (!email) return 'Utilisateur';
    return email.split('@')[0];
  });

  constructor() {
    // Keep currentUrl in sync with router navigation for active-state styling.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  protected isActive(route?: string): boolean {
    if (!route) return false;
    const u = this.currentUrl();
    return u === route || u.startsWith(route + '/');
  }

  protected navigate(route?: string): void {
    if (!route) return;
    this.routeNavigator.toUrl(route);
    // Close the mobile drawer after navigating so it doesn't cover the page.
    this.appShell().closeMobile();
  }

  // ── Topbar dropdown handlers ───────────────────────────────────────────────

  protected toggleNotifications(): void {
    this.showNotifications.update((v) => !v);
    this.showProfile.set(false);
  }

  protected toggleProfile(): void {
    this.showProfile.update((v) => !v);
    this.showNotifications.set(false);
  }

  /** Closes all topbar dropdowns. Wired to Escape via host binding. */
  protected closeAllMenus(): void {
    this.showNotifications.set(false);
    this.showProfile.set(false);
  }

  /** Closes menus when clicking outside any topbar item. Wired to document:click. */
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.gz-topbar-item')) {
      this.closeAllMenus();
    }
  }

  protected goToSettings(): void {
    this.routeNavigator.toUrl('/settings');
    this.showProfile.set(false);
  }

  protected onLogout(): void {
    this.authService.logout();
    this.showProfile.set(false);
  }
}
